// Reusable avatar: shows uploaded photo, a deterministic cartoon (DiceBear)
// or a colourful single-initial fallback. Behaviour picked by `fallback`:
//   • `cartoon` (default) — auto-generates a DiceBear "fun-emoji" URL from
//     the user's name so everyone gets a recognisable face without extra
//     config. Overridable per-user by uploading a real photo.
//   • `initial` — legacy single-letter tile with a hashed background colour.
// The cartoon URL is deterministic so the same user always sees the same
// face across sessions — until they explicitly randomize it.
import { useState } from "react";
import { mysteryAvatarFor } from "@/lib/mysteryAvatars";

const PALETTE = [
  "#6366f1", "#0891b2", "#16a34a", "#ea580c", "#db2777",
  "#7c3aed", "#2563eb", "#d97706", "#dc2626", "#0d9488",
];

function initial(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[0][0].toUpperCase() : "?";
}

function toneFor(name = "") {
  let h = 0;
  for (const c of name) h = (h + c.charCodeAt(0)) % PALETTE.length;
  return PALETTE[h];
}

// DiceBear "fun-emoji" style — SVG cartoon faces, no API key, CDN-cached.
// Seed with the user's name so the face is consistent across sessions.
export function cartoonUrlFor(seed = "") {
  const s = encodeURIComponent((seed || "conduit").trim().toLowerCase());
  return `https://api.dicebear.com/9.x/fun-emoji/svg?seed=${s}&backgroundType=gradientLinear&radius=50`;
}

const SIZES = { sm: "h-8 w-8 text-xs", md: "h-11 w-11 text-sm", lg: "h-14 w-14 text-base", xl: "h-16 w-16 text-lg" };

export const Avatar = ({ name = "", src, size = "md", className = "", testid, fallback = "cartoon" }) => {
  const [failed, setFailed] = useState(false);
  const cls = `relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white ring-2 ring-white shadow-sm ${SIZES[size] || SIZES.md} ${className}`;
  const explicit = src && !failed;
  if (explicit) {
    return <img src={src} alt={name} data-testid={testid} onError={() => setFailed(true)} className={`${cls} object-cover`} />;
  }
  if (fallback === "mystery") {
    // Mystery-gang style default (Who's In board) until a real photo lands.
    // Nudged up and in slightly so the face fills the circle at small sizes.
    return (
      <img src={mysteryAvatarFor(name)} alt={name} data-testid={testid}
           style={{ objectPosition: "center 28%", transform: "scale(1.12)" }}
           className={`${cls} object-cover bg-slate-100`} />
    );
  }
  if (fallback === "cartoon") {
    // Rendered as an <img> so users can still upload / override anytime.
    return (
      <img src={cartoonUrlFor(name)} alt={name} data-testid={testid}
           className={`${cls} object-cover bg-slate-100`} />
    );
  }
  return (
    <span data-testid={testid} className={cls} style={{ backgroundColor: toneFor(name) }}>
      {initial(name)}
    </span>
  );
};
